import axios from "axios";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "../../../env";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import Sidebar from "../../../components/layout/Sidebar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import CustomPagination from "../../../components/ui/CustomPagination";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import { IoSearch } from "react-icons/io5";
import { IoMdAdd } from "react-icons/io";
import { IoEyeSharp } from "react-icons/io5";
import { MdModeEdit, MdDelete } from "react-icons/md";

const EntityDetails = ({ entity: routeEntity }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { adminUser } = useAdminAuth();
  const [data, setData] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  // App.js already passes the entity for each list route. Falling back to it
  // means the list still loads when it is opened without navigation state
  // (a typed or bookmarked URL, or a link that forgot to pass state).
  const entity = location.state?.entity ?? routeEntity;
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 992);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  const entityMap = useMemo(() => {
    const fundingAgenciesEndpoint = `${BASE_URL}/api/getModelData/funding_agencies?fields=id,agencyName`;

    return {
      fundingAgencies: {
        title: "Funding Agencies",
        navigationPath: "addEditFundingAgency",
        apiEndpoint: fundingAgenciesEndpoint,
        delApiEndPoint: `${BASE_URL}/api/delDetails/`,
        view: true,
        edit: true,
        del: true,
        model: "funding_agencies",
      },
      dailyGoldRate: {
        title: "Daily Gold Rate",
        navigationPath: "addEditDailyGoldRate",
        apiEndpoint: `${BASE_URL}/api/getModelData/daily_gold_rate?fields=id,rateDate,goldRate22k`,
        delApiEndPoint: `${BASE_URL}/api/delDetails/`,
        view: true,
        edit: true,
        del: false,
        model: "daily_gold_rate",
      },
    };
  }, [adminUser, BASE_URL]);

  useEffect(() => {
    if (entityMap[entity]) {
      setSelectedEntity(entityMap[entity]);
      fetchData(entityMap[entity].apiEndpoint);
    } else {
      console.error("Invalid entity parameter");
    }
  }, [entity, entityMap]);

  const fetchData = async (apiEndpoint) => {
    try {
      const response = await axios.get(apiEndpoint);
      const formattedData = entity === 'dailyGoldRate'
        ? response.data.data.map(item => ({
          id: item.id,
          Date: new Date(item.rateDate).toLocaleDateString('en-GB'),
          'Gold Rate (22k)': item.goldRate22k
        }))
        : response.data.data;
      setData(formattedData || []);
    } catch (error) {
      console.error(`Error fetching data:`, error);
      toast.error("Failed to load data");
    }
  };

  const handleAddClick = () => {
    if (selectedEntity) {
      navigate(`/${selectedEntity.navigationPath}`, {
        state: { type: "Add", entity },
      });
    }
  };

  const handleViewClick = (id) => {
    if (selectedEntity) {
      navigate(`/${selectedEntity.navigationPath}`, {
        state: { type: "View", id, entity },
      });
    }
  };

  const handleEditClick = (id) => {
    if (selectedEntity) {
      navigate(`/${selectedEntity.navigationPath}`, {
        state: { type: "Edit", id, entity },
      });
    }
  };

  const handleDelClick = (id) => {
    setSelectedId(id);
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedEntity || !selectedEntity.delApiEndPoint) return;

    const payload = {
      model: selectedEntity.model,
      where: { id: selectedId },
    };

    try {
      const response = await axios.post(selectedEntity.delApiEndPoint, payload);
      if (response.status === 200) {
        toast.success("Record deleted successfully!");
        fetchData(selectedEntity.apiEndpoint);
      } else {
        toast.error("Failed to delete record.");
      }
    } catch (error) {
      console.error("Error during deletion:", error);
      toast.error("An error occurred while deleting the record.");
    } finally {
      setShowModal(false);
      setSelectedId(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedId(null);
  };

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return data;
    const q = searchQuery.toLowerCase().trim();
    return data.filter(item =>
      Object.entries(item).some(([k, v]) => {
        if (k === 'id') return false;
        return String(v || '').toLowerCase().includes(q);
      })
    );
  }, [data, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const indexOfFirstRow = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(indexOfFirstRow, indexOfFirstRow + rowsPerPage);
  const columns = data.length > 0 ? Object.keys(data[0]).filter(k => k !== 'id') : [];

  if (!selectedEntity) {
    return <div>Loading...</div>;
  }

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section inline-header">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <h1 className="TableHeaderText m-0">{selectedEntity.title}</h1>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder={`Search ${selectedEntity.title}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                <span className="search-icon">
                  <IoSearch />
                </span>
              </div>
            </div>

            <button className="btn btn-addCredential m-0" onClick={handleAddClick}>
              <IoMdAdd />
              Add {selectedEntity.title}
            </button>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper table-responsive">
              <table className="mx-auto table table-striped table-list">
                <thead>
                  <tr>
                    <th style={{ width: "70px" }}>No</th>
                    {columns.map((col, idx) => (
                      <th key={idx}>{col.toUpperCase()}</th>
                    ))}
                    {(selectedEntity.view || selectedEntity.edit || selectedEntity.del) && (
                      <th style={{ width: "160px" }}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {currentData.length > 0 ? (
                    currentData.map((row, index) => (
                      <tr key={row.id || index}>
                        <td>{indexOfFirstRow + index + 1}</td>
                        {columns.map((col, idx) => (
                          <td key={idx} className="fw-semibold">
                            {row[col] ?? "-"}
                          </td>
                        ))}
                        {(selectedEntity.view || selectedEntity.edit || selectedEntity.del) && (
                          <td>
                            <div className="d-flex justify-content-center align-items-center gap-2">
                              {selectedEntity.view && (
                                <button
                                  className="btn btn-view"
                                  title="View"
                                  onClick={() => handleViewClick(row.id)}
                                >
                                  <IoEyeSharp />
                                </button>
                              )}
                              {selectedEntity.edit && (
                                <button
                                  className="btn btn-edit"
                                  title="Edit"
                                  onClick={() => handleEditClick(row.id)}
                                >
                                  <MdModeEdit />
                                </button>
                              )}
                              {selectedEntity.del && (
                                <button
                                  className="btn btn-delete"
                                  title="Delete"
                                  onClick={() => handleDelClick(row.id)}
                                >
                                  <MdDelete />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length + 2} className="text-center py-4">
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={(page) => setCurrentPage(page)}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={showModal}
        handleClose={handleCloseModal}
        handleConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this record?"
      />
      <ToastContainer position="top-center" />
    </section>
  );
};

export default EntityDetails;
